import _ from "lodash";

import { NormalizeString } from "./normalize-string/NormalizeString";

/**
 * Rótulo de empresa-membro nas telas de grupo.
 *
 * O CNPJ não é enfeite: grupos de filiais compartilham o MESMO `name` — as seis empresas do grupo
 * TRANSREFER se chamam todas "TRANSREFER TRANSPORTE E LOGISTICA LTDA" e diferem só no documento.
 * Sem ele, a linha por empresa da DRE e do Balanço fica indistinguível das outras cinco.
 *
 * Usa o `formatDocument` que já existe no projeto, para o CNPJ sair igual ao resto do sistema.
 */
export function groupCompanyLabel(company = {}) {
    const name = String(company.name || "").trim() || "Empresa sem nome";
    const document = NormalizeString.formatDocument(company.document);
    return document ? `${name} - ${document}` : name;
}

/**
 * Rótulo da LINHA de empresa numa tabela, onde a coluna de conta é estreita.
 *
 * Aqui o documento vem primeiro de propósito. O nome é idêntico nas seis filiais e é o CNPJ que
 * distingue — com o nome à frente, a truncagem da coluna come exatamente a parte que identifica,
 * e as seis linhas ficam indistinguíveis. Invertido, o que sobra na tela é sempre o que importa.
 */
export function groupCompanyRowLabel(company = {}) {
    const name = String(company.name || "").trim() || "Empresa sem nome";
    const document = NormalizeString.formatDocument(company.document);
    return document ? `${document} · ${name}` : name;
}

/** `"<conta> - <empresa> - <CNPJ>"`, a convenção de nome das séries de empresa no grupo. */
export function withGroupCompanyLabel(base, company) {
    return `${base} - ${groupCompanyLabel(company)}`;
}

/**
 * Ordena as empresas-membro do grupo. Desempata pelo DOCUMENTO, e não só pelo nome: num grupo de
 * filiais todos os nomes são iguais, e sem o desempate a lista sai na ordem que a API devolveu —
 * 0005, 0006, 0001, 0004...
 */
export function sortGroupCompanies(companies = []) {
    return [...companies].sort(
        (a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""), "pt-BR", {
                sensitivity: "base"
            }) ||
            String(a?.document || "").localeCompare(String(b?.document || ""))
    );
}

/**
 * A empresa casa com o termo buscado — por nome OU por CNPJ.
 *
 * Buscar por documento não é conveniência: num grupo de filiais os nomes são idênticos, e digitar
 * "TRANSREFER" na busca devolve as seis. O CNPJ é a única forma de achar uma delas, e o cliente o
 * digita como está acostumado — com ponto, barra e traço —, então os dois lados são comparados só
 * pelos dígitos.
 */
export function matchesGroupCompanySearch(company = {}, term = "") {
    const search = String(term || "").trim();
    if (!search) return true;

    // Mesma normalização que a busca da tela já usava, para o acento não mudar o resultado.
    const name = _.deburr(String(company.name || "")).toUpperCase();
    if (name.includes(_.deburr(search).toUpperCase())) return true;

    const searchDigits = search.replace(/\D/g, "");
    if (!searchDigits) return false;

    const document = String(company.document || "").replace(/\D/g, "");
    return document.includes(searchDigits);
}
